<script lang="ts" context="module">
  import type { ReportType } from '../lib/api';

  interface ReportGroup {
    key: string | null;
    displayName: string;
    reports: ReportType[];
  }
</script>

<script lang="ts">
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import { api } from '../lib/api';
  import { BarChart3 } from 'lucide-svelte';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import { Card, Spinner, Badge } from '../lib/components/ui/index';

  let reportTypes: ReportType[] = [];
  let loading = true;
  let error = '';

  $: groups = groupReportTypes(reportTypes);

  function groupReportTypes(types: ReportType[]): ReportGroup[] {
    const groupMap = new Map<string | null, ReportGroup>();

    for (const rt of types) {
      const key = rt.group ?? null;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          key,
          displayName: rt.groupDisplayName ?? $_('reports.ungrouped'),
          reports: [],
        });
      }
      groupMap.get(key)!.reports.push(rt);
    }

    // Named groups first, ungrouped last
    const named = [...groupMap.values()].filter((g) => g.key !== null);
    const ungrouped = groupMap.get(null);
    return ungrouped ? [...named, ungrouped] : named;
  }

  onMount(async () => {
    try {
      reportTypes = await api.reports.getReportTypes();
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Failed to load report types';
    } finally {
      loading = false;
    }
  });
</script>

<PageHeader title={$_('reports.title')} subtitle={$_('reports.subtitle')} />

{#if loading}
  <div class="flex justify-center py-16">
    <Spinner size="lg" label={$_('messages.loading')} />
  </div>
{:else if error}
  <div class="px-3 py-2 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
    {error}
  </div>
{:else if reportTypes.length === 0}
  <div class="text-center py-16 bg-surface border border-border rounded-lg">
    <BarChart3 class="w-10 h-10 text-text-muted mx-auto mb-3" strokeWidth={1.5} />
    <p class="text-sm text-text-muted">{$_('reports.no_report_types')}</p>
  </div>
{:else}
  <div class="space-y-6">
    {#each groups as group (group.displayName)}
      <div>
        <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
          {group.displayName}
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {#each group.reports as rt (rt.reportType)}
            <Link to="/reports/{rt.reportType}" class="block">
              <div
                class="bg-surface border border-border rounded-lg p-5 transition-all hover:border-accent/50 hover:shadow-lg hover:shadow-accent/5 hover:-translate-y-0.5 group"
              >
                <h3
                  class="text-base font-semibold text-accent group-hover:text-accent-hover mb-1.5"
                >
                  {rt.displayName}
                </h3>
                <p class="text-xs text-text-muted mb-3">
                  {rt.fields.length}
                  {$_('reports.filter_fields')}
                </p>
                <div class="flex flex-wrap gap-1.5">
                  {#each rt.fields.slice(0, 3) as field (field.name)}
                    <Badge variant={field.required ? 'info' : 'default'}>
                      {field.displayName}
                    </Badge>
                  {/each}
                </div>
              </div>
            </Link>
          {/each}
        </div>
      </div>
    {/each}
  </div>
{/if}
